import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Switch,
  CircularProgress, Alert, Tooltip, alpha, useTheme, Fade, Card,
  Stack, Divider,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon,
  Delete as DeleteIcon, Refresh as RefreshIcon, Category as CategoryIcon,
  Close as CloseIcon, CheckCircle as ActiveIcon, Cancel as InactiveIcon,
  Inventory as ProductCountIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { tradesService, CostCategory, CostCategoryPayload } from '../../services/tradesService';

// ── Permission helper ─────────────────────────────────────────────────────────
const usePermissions = () => {
  const { user } = useAuth();
  const perms = user?.role?.permissions?.[0];
  const isDataManager = user?.role?.role_name === 'DATA_MANAGER';
  return {
    canCreate: perms?.create_permission ?? false,
    canUpdate: perms?.update_permission ?? false,
    canDelete: (perms?.delete_permission ?? false) || isDataManager || (user?.is_superuser ?? false),
  };
};

// ── Form dialog ───────────────────────────────────────────────────────────────
interface FormValues { cost_category_name: string; description: string; is_active: boolean; }

const CategoryFormDialog: React.FC<{
  open: boolean;
  editing: CostCategory | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, editing, onClose, onSaved }) => {
  const theme = useTheme();
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');
  const { user } = useAuth();

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    defaultValues: { cost_category_name: '', description: '', is_active: true },
  });

  useEffect(() => {
    if (open) {
      setApiError('');
      reset(editing
        ? { cost_category_name: editing.cost_category_name, description: editing.description || '', is_active: editing.is_active }
        : { cost_category_name: '', description: '', is_active: true }
      );
    }
  }, [open, editing, reset]);

  const onSubmit = async (data: FormValues) => {
    setSaving(true);
    setApiError('');
    try {
      const payload: CostCategoryPayload = {
        cost_category_name: data.cost_category_name.trim(),
        description: data.description.trim(),
        is_active: data.is_active,
      };
      if (editing) {
        await tradesService.updateCostCategory(editing.cost_category_id, payload);
      } else {
        await tradesService.createCostCategory(payload);
      }
      onSaved();
      onClose();
    } catch (e: any) {
      const errs = e?.data?.errors;
      setApiError(
        errs?.cost_category_name?.[0] || errs?.non_field_errors?.[0] ||
        e?.data?.message || 'Something went wrong.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}>
      <Box sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CategoryIcon sx={{ color: 'white', fontSize: 22 }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
            {editing ? 'Edit Category' : 'Add New Category'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white', p: 0.5 }}><CloseIcon /></IconButton>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ px: 3, py: 3 }}>
          {apiError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{apiError}</Alert>}

          <Stack spacing={2.5}>
            <Controller name="cost_category_name" control={control}
              rules={{ required: 'Category name is required', maxLength: { value: 200, message: 'Max 200 characters' } }}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Category Name *" error={!!errors.cost_category_name}
                  helperText={errors.cost_category_name?.message}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              )} />

            <Controller name="description" control={control}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Description" multiline rows={3}
                  placeholder="Optional description..."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              )} />

            <Controller name="is_active" control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch {...field} checked={field.value}
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#667eea' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#667eea' } }} />}
                  label={<Typography variant="body2" fontWeight={600}>
                    {field.value ? 'Active' : 'Inactive'}
                  </Typography>}
                />
              )} />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={onClose} variant="outlined" disabled={saving}
            sx={{ borderRadius: 2, borderColor: alpha('#667eea', 0.4), color: '#667eea',
              '&:hover': { borderColor: '#667eea', background: alpha('#667eea', 0.05) } }}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4292 100%)' } }}>
            {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ── Delete confirm ────────────────────────────────────────────────────────────
const DeleteDialog: React.FC<{
  open: boolean; item: CostCategory | null;
  onClose: () => void; onDeleted: () => void;
}> = ({ open, item, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    if (!item) return;
    setDeleting(true); setError('');
    try {
      await tradesService.deleteCostCategory(item.cost_category_id);
      onDeleted(); onClose();
    } catch (e: any) {
      setError(e?.data?.message || 'Failed to delete. The category may have linked products.');
    } finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Box sx={{ width: 60, height: 60, borderRadius: '50%', background: alpha('#f44336', 0.1),
          display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
          <DeleteIcon sx={{ color: '#f44336', fontSize: 28 }} />
        </Box>
        <Typography variant="h6" fontWeight={700} mb={1}>Delete Category</Typography>
        <Typography variant="body2" color="text.secondary" mb={1}>
          Are you sure you want to delete <strong>"{item?.cost_category_name}"</strong>?
          This action cannot be undone.
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 1, borderRadius: 2, textAlign: 'left' }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={deleting}
          sx={{ borderRadius: 2, minWidth: 100 }}>Cancel</Button>
        <Button onClick={handleDelete} variant="contained" disabled={deleting} color="error"
          startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          sx={{ borderRadius: 2, minWidth: 100 }}>
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const CostCategoriesPage: React.FC = () => {
  const theme = useTheme();
  const { canCreate, canUpdate, canDelete } = usePermissions();

  const [categories, setCategories] = useState<CostCategory[]>([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filterActive, setFilterActive] = useState<string>('');

  const [formOpen, setFormOpen]       = useState(false);
  const [editing, setEditing]         = useState<CostCategory | null>(null);
  const [deleteOpen, setDeleteOpen]   = useState(false);
  const [deleting, setDeleting]       = useState<CostCategory | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      if (filterActive !== '') params.is_active = filterActive === 'true';
      const res = await tradesService.listCostCategories(params);
      setCategories(res.results);
    } catch { } finally { setLoading(false); }
  }, [search, filterActive]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit   = (c: CostCategory) => { setEditing(c); setFormOpen(true); };
  const openDelete = (c: CostCategory) => { setDeleting(c); setDeleteOpen(true); };

  return (
    <Fade in timeout={400}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography variant="h5" fontWeight={800}
              sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Cost Categories
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.25}>
              Manage Cost classification categories
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData} size="small"
                sx={{ border: `1px solid ${alpha('#667eea', 0.3)}`, color: '#667eea',
                  '&:hover': { background: alpha('#667eea', 0.08) } }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canCreate && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} size="small"
                sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': { background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4292 100%)' },
                  boxShadow: '0 4px 12px rgba(102,126,234,0.3)' }}>
                Add Category
              </Button>
            )}
          </Stack>
        </Box>

        {/* Filters */}
        <Card sx={{ p: 2, mb: 2.5, borderRadius: 3, border: `1px solid ${alpha('#667eea', 0.1)}` }} elevation={0}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField size="small" placeholder="Search categories..." value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: 260, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <Stack direction="row" spacing={1}>
              {[{ label: 'All', value: '' }, { label: 'Active', value: 'true' }, { label: 'Inactive', value: 'false' }].map(f => (
                <Chip key={f.value} label={f.label} size="small" clickable
                  onClick={() => setFilterActive(f.value)}
                  sx={{
                    fontWeight: 600, borderRadius: 1.5,
                    background: filterActive === f.value ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : alpha('#667eea', 0.07),
                    color: filterActive === f.value ? 'white' : '#667eea',
                  }} />
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {categories.length} result{categories.length !== 1 ? 's' : ''}
            </Typography>
          </Stack>
        </Card>

        {/* Table */}
        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha('#667eea', 0.1)}`, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: alpha('#667eea', 0.04) }}>
                  {['#', 'Cost Category Name', 'Description', 'Status', 'Created By', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', color: 'text.secondary',
                      py: 1.5, borderBottom: `2px solid ${alpha('#667eea', 0.12)}`, whiteSpace: 'nowrap' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={32} sx={{ color: '#667eea' }} />
                    </TableCell>
                  </TableRow>
                ) : categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <CategoryIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">No categories found</Typography>
                    </TableCell>
                  </TableRow>
                ) : categories.map((cat, idx) => (
                  <TableRow key={cat.cost_category_id}
                    sx={{ '&:hover': { background: alpha('#667eea', 0.03) }, transition: 'background 0.2s' }}>
                    <TableCell sx={{ py: 1.5, color: 'text.secondary', fontSize: '0.8rem' }}>{idx + 1}</TableCell>
                    <TableCell sx={{ py: 1.5, fontWeight: 600, fontSize: '0.875rem' }}>{cat.cost_category_name}</TableCell>
                    <TableCell sx={{ py: 1.5, color: 'text.secondary', fontSize: '0.8rem', maxWidth: 200 }}>
                      <Typography variant="body2" noWrap title={cat.description || ''}>
                        {cat.description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Chip size="small"
                        icon={cat.is_active ? <ActiveIcon style={{ fontSize: 13 }} /> : <InactiveIcon style={{ fontSize: 13 }} />}
                        label={cat.is_active ? 'Active' : 'Inactive'}
                        sx={{
                          background: cat.is_active ? alpha('#4CAF50', 0.1) : alpha('#f44336', 0.1),
                          color: cat.is_active ? '#4CAF50' : '#f44336',
                          fontWeight: 700, height: 22, fontSize: '0.75rem',
                        }} />
                    </TableCell>
                    <TableCell sx={{ py: 1.5, color: 'text.secondary', fontSize: '0.78rem' }}>{cat.created_by}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <Stack direction="row" spacing={0.5}>
                        {canUpdate && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(cat)}
                              sx={{ color: '#667eea', '&:hover': { background: alpha('#667eea', 0.1) } }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => openDelete(cat)}
                              sx={{ color: '#f44336', '&:hover': { background: alpha('#f44336', 0.1) } }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <CategoryFormDialog open={formOpen} editing={editing}
          onClose={() => setFormOpen(false)} onSaved={fetchData} />
        <DeleteDialog open={deleteOpen} item={deleting}
          onClose={() => setDeleteOpen(false)} onDeleted={fetchData} />
      </Box>
    </Fade>
  );
};

export default CostCategoriesPage;